/**
 * Organisator des offenen Termins.
 *
 * Eine Assistenz mit Bearbeitungsrecht öffnet den Termin im Kalender der
 * vorgesetzten Person: Outlook führt dort die vorgesetzte Person als
 * Organisator, die Assistenz ist nur die angemeldete Person. Der Organisator
 * ist damit die zweite Person, die als Gastgeber in Frage kommt — und die
 * einzige, die sonst nirgends auftaucht, denn aus der Teilnehmerliste ist sie
 * bewusst entfernt.
 *
 * Zwei Quellen, in dieser Reihenfolge:
 *  1. `item.organizer` — im Bearbeiten-Modus ein Objekt mit `getAsync`
 *     (Mailbox 1.7), im Lese-Modus direkt die Adresse.
 *  2. `getSharedPropertiesAsync().owner` — das Postfach, in dem der Termin
 *     liegt. Nur in freigegebenen Ordnern verfügbar (Mailbox 1.8) und damit
 *     der Rückfall, wenn der Client `organizer` beim Bearbeiten nicht kennt.
 *
 * Löst immer auf — ist keine der Quellen verfügbar, mit `null`.
 */
export async function getAppointmentOrganizer() {
  try {
    return (await readOrganizer()) || (await readMailboxOwner());
  } catch (e) {
    return null;
  }
}

function toEntry(details) {
  if (!details) return null;
  const email = String(typeof details === "string" ? details : details.emailAddress || "").trim();
  if (!email) return null;
  return { email, name: String(details.displayName || "").trim() };
}

function readOrganizer() {
  return new Promise((resolve) => {
    try {
      const organizer = Office.context.mailbox.item?.organizer;
      if (!organizer) {
        resolve(null);
        return;
      }
      // Lese-Modus: `organizer` ist bereits das Adressobjekt.
      if (typeof organizer.getAsync !== "function") {
        resolve(toEntry(organizer));
        return;
      }
      organizer.getAsync((result) => {
        resolve(result.status === Office.AsyncResultStatus.Succeeded ? toEntry(result.value) : null);
      });
    } catch (e) {
      resolve(null);
    }
  });
}

/**
 * Besitzer des Postfachs, in dem der Termin geöffnet ist. Ausserhalb eines
 * freigegebenen Ordners schlägt der Aufruf fehl — das bleibt hier bewusst
 * still, dann gibt es schlicht keinen fremden Kalender.
 */
export function readMailboxOwner() {
  return new Promise((resolve) => {
    try {
      const item = Office.context.mailbox.item;
      if (typeof item?.getSharedPropertiesAsync !== "function") {
        resolve(null);
        return;
      }
      item.getSharedPropertiesAsync((result) => {
        resolve(result.status === Office.AsyncResultStatus.Succeeded ? toEntry(result.value?.owner) : null);
      });
    } catch (e) {
      resolve(null);
    }
  });
}
